import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rss, Send, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * ShareNotificationButton - Bouton pour créer une notification à partir d'un contexte
 * 
 * Notifications contextuelles (avec confirmation et ciblage automatique):
 * - facture: vers le cavalier lié
 * - cours/stage (avec groupe): vers les cavaliers du groupe
 * - soins_cheval: vers les cavaliers liés au cheval
 * - statut_cavalier: vers le cavalier concerné
 * - groupe: vers les cavaliers du groupe
 * - inscription_cours_instructeur: vers le cavalier concerné
 * - inscription_cours_cavalier: vers l'instructeur concerné
 * 
 * Autres cas: ouvre l'éditeur de notification
 */
export const ShareNotificationButton = ({ 
  contextType, 
  contextId,
  contextLabel = '',
  // Données supplémentaires pour le ciblage automatique
  targetUserId = null,        // ID du cavalier ciblé (facture, statut, inscription)
  targetUserName = null,      // Nom du cavalier ciblé
  targetGroupeId = null,      // ID du groupe ciblé (cours, groupe)
  targetGroupeName = null,    // Nom du groupe
  targetChevalId = null,      // ID du cheval (soins)
  targetChevalName = null,    // Nom du cheval
  targetInstructeurId = null, // ID de l'instructeur (inscription par cavalier)
  defaultTitre = '',
  defaultContenu = '',
  variant = 'icon',
  size = 'sm',
  className = ''
}) => {
  const navigate = useNavigate();
  const { isAdmin, isInstructor } = useAuth();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [notificationData, setNotificationData] = useState({
    titre: defaultTitre,
    contenu: defaultContenu
  });
  
  // Determine if this is a contextual notification with auto-targeting
  const getAutoTargeting = () => {
    switch (contextType) {
      case 'facture':
        if (targetUserId) {
          return { type: 'user', userId: targetUserId, userName: targetUserName, label: `Cavalier: ${targetUserName}` };
        }
        break;
      case 'cours':
      case 'stage':
        if (targetGroupeId) {
          return { type: 'groupe', groupeId: targetGroupeId, groupeName: targetGroupeName, label: `Groupe: ${targetGroupeName}` };
        }
        break;
      case 'soins_cheval':
        if (targetChevalId) {
          return { type: 'cheval', chevalId: targetChevalId, chevalName: targetChevalName, label: `Cavaliers liés au cheval: ${targetChevalName}` };
        }
        break;
      case 'statut_cavalier':
        if (targetUserId) {
          return { type: 'user', userId: targetUserId, userName: targetUserName, label: `Cavalier: ${targetUserName}` };
        }
        break;
      case 'groupe':
        if (targetGroupeId) {
          return { type: 'groupe', groupeId: targetGroupeId, groupeName: targetGroupeName, label: `Groupe: ${targetGroupeName}` };
        }
        break;
      case 'inscription_cours_instructeur':
        // Instructor modifies enrollment -> notify the rider
        if (targetUserId) {
          return { type: 'user', userId: targetUserId, userName: targetUserName, label: `Cavalier: ${targetUserName}` };
        }
        break;
      case 'inscription_cours_cavalier':
        // Rider modifies enrollment -> notify the instructor
        if (targetInstructeurId) {
          return { type: 'instructeur', instructeurId: targetInstructeurId, label: 'Instructeur concerné' };
        }
        break;
      default:
        return null;
    }
    return null;
  };
  
  const autoTargeting = getAutoTargeting();
  
  // Only admin/instructors can create notifications
  if (!isAdmin() && !isInstructor()) {
    return null;
  }
  
  const handleClick = (e) => {
    e.stopPropagation();
    
    if (autoTargeting) {
      // Show confirmation modal for contextual notifications
      setNotificationData({
        titre: defaultTitre || contextLabel || '',
        contenu: defaultContenu || ''
      });
      setShowConfirmModal(true);
    } else {
      // Open editor for non-contextual notifications
      const params = new URLSearchParams({
        context_type: contextType,
        ...(contextId && { context_id: contextId }),
        ...(defaultTitre && { titre: defaultTitre })
      });
      navigate(`/notifications/admin?${params}`);
    }
  };
  
  const handleSendContextual = async () => {
    if (!notificationData.titre.trim()) {
      toast.error('Le titre est obligatoire');
      return;
    }
    
    setSending(true);
    try {
      // Create and publish notification with auto-targeting
      const payload = {
        titre: notificationData.titre,
        contenu: notificationData.contenu || notificationData.titre,
        category: getCategoryFromContext(contextType),
        targeting: buildTargeting(autoTargeting),
        context_type: contextType,
        context_id: contextId,
        auto_publish: true // Automatically publish
      };
      
      await axios.post(`${API_URL}/api/admin/notifications/contextual`, payload);
      toast.success('Notification envoyée');
      setShowConfirmModal(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };
  
  const getCategoryFromContext = (type) => {
    switch (type) {
      case 'facture': return 'facturation';
      case 'cours':
      case 'stage':
      case 'inscription_cours_instructeur':
      case 'inscription_cours_cavalier':
        return 'cours';
      case 'soins_cheval': return 'soins';
      case 'groupe':
      case 'statut_cavalier':
      default:
        return 'vie_club';
    }
  };
  
  const buildTargeting = (target) => {
    if (!target) return { all_riders: true };
    
    switch (target.type) {
      case 'user':
        return { user_ids: [target.userId] };
      case 'groupe':
        return { groupe_ids: [target.groupeId] };
      case 'cheval':
        return { cheval_id: target.chevalId };
      case 'instructeur':
        return { user_ids: [target.instructeurId] };
      default:
        return { all_riders: true };
    }
  };
  
  const handleOpenEditor = () => {
    setShowConfirmModal(false);
    const params = new URLSearchParams({
      context_type: contextType,
      ...(contextId && { context_id: contextId }),
      ...(notificationData.titre && { titre: notificationData.titre })
    });
    navigate(`/notifications/admin?${params}`);
  };
  
  const renderButton = () => {
    if (variant === 'icon') {
      return (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={handleClick}
                className={`p-2 rounded-lg border border-stone-200 text-stone-500 hover:text-[#2E4A3D] hover:bg-stone-100 hover:border-[#2E4A3D] transition-colors ${className}`}
                data-testid={`share-notif-${contextType}-${contextId || 'new'}`}
              >
                <Rss className="w-4 h-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Créer une notification</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }
    
    return (
      <Button
        variant="outline"
        size={size}
        onClick={handleClick}
        className={className}
        data-testid={`share-notif-${contextType}-${contextId || 'new'}`}
      >
        <Rss className="w-4 h-4 mr-2" />
        Notifier
      </Button>
    );
  };
  
  return (
    <>
      {renderButton()}
      
      {/* Confirmation Modal for Contextual Notifications */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center pt-4 sm:pt-0 overflow-y-auto">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowConfirmModal(false)} />
          
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 my-4">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-stone-200">
              <h3 className="font-semibold text-stone-900">Créer une notification</h3>
              <button
                onClick={() => setShowConfirmModal(false)}
                className="p-1 hover:bg-stone-100 rounded-full"
              >
                <X className="w-5 h-5 text-stone-500" />
              </button>
            </div>
            
            {/* Content */}
            <div className="p-4 space-y-4">
              {/* Auto-targeting info */}
              {autoTargeting && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>Destinataire(s) :</strong> {autoTargeting.label}
                  </p>
                </div>
              )}
              
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Titre *
                </label>
                <input
                  type="text"
                  value={notificationData.titre}
                  onChange={(e) => setNotificationData(prev => ({ ...prev, titre: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#2E4A3D] focus:border-transparent"
                  placeholder="Titre de la notification"
                />
              </div>
              
              {/* Content */}
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  Message (optionnel)
                </label>
                <textarea
                  value={notificationData.contenu}
                  onChange={(e) => setNotificationData(prev => ({ ...prev, contenu: e.target.value }))}
                  className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-[#2E4A3D] focus:border-transparent resize-none"
                  rows={3}
                  placeholder="Message détaillé..."
                />
              </div>
            </div>
            
            {/* Footer */}
            <div className="flex items-center justify-between p-4 border-t border-stone-200 bg-stone-50 rounded-b-xl">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleOpenEditor}
                className="text-stone-600"
              >
                Ouvrir l'éditeur
              </Button>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Annuler
                </Button>
                <Button
                  size="sm"
                  onClick={handleSendContextual}
                  disabled={sending || !notificationData.titre.trim()}
                  className="bg-[#2E4A3D] hover:bg-[#243D32]"
                >
                  {sending ? (
                    'Envoi...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Envoyer
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareNotificationButton;
